import React, {Fragment, useContext, useEffect} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import {SVGIcon} from '@homeaway/react-svg';
import {ButtonPrimary, Button} from '@homeaway/react-buttons';

import ModalHeading from '../ModalHeading/ModalHeading';

import {CHECK__32} from './ModalIcon';
import StayContext from '../../../../../context/StayContext';
import SystemContext from '../../../../../context/SystemContext';
import {
    getLocalizedDate,
    SHORT_DATE_FORMAT,
    LONG_DATE_FORMAT,
} from '../../../../../../utils/dates';
import {tripMessageSetSelected} from '../../../../../../utils/EDAP/Conversation';
import {
    MODIFY_BOOKING,
    MODIFY_REQUEST,
    modifyBookingRequestConfirmationPresented,
} from '../../../../../../utils/EDAP/ConversationDetails';
import {
    getAdultsLocalizationKey,
    getChildrenLocalizationKey,
} from '../../../../../../utils/guests';
import translate from '../../../../../../utils/translations';

import './ModifyConfirmation.less';

export default function ModifyConfirmation({
    onCloseModal,
    requestedFields,
    translations,
}) {
    const {
        stay: {
            booking: {
                arrival: oldCheckInDate,
                departure: oldCheckOutDate,
                guests: {
                    numberOfAdults: oldAdultCount,
                    numberOfChildren: oldChildCount,
                },
                reservationUuid,
            },
            conversation: {id: conversationId},
        },
        backUrl,
        refetchConversation,
    } = useContext(StayContext);
    const {
        system: {fullLocale},
    } = useContext(SystemContext);
    const {
        adults: newAdultCount,
        children: newChildCount,
        checkInDate: newCheckInDate,
        checkOutDate: newCheckOutDate,
        notes,
    } = requestedFields || {};

    const getOldDates = () => {
        const localizedOldCheckInDate = getLocalizedDate(
            oldCheckInDate,
            fullLocale,
            SHORT_DATE_FORMAT
        );
        const localizedOldCheckOutDate = getLocalizedDate(
            oldCheckOutDate,
            fullLocale,
            LONG_DATE_FORMAT
        );
        return (
            <div>
                <s>{`${localizedOldCheckInDate} - ${localizedOldCheckOutDate}`}</s>
            </div>
        );
    };

    const getNewDates = () => {
        const localizedNewCheckInDate = getLocalizedDate(
            newCheckInDate,
            fullLocale,
            SHORT_DATE_FORMAT
        );
        const localizedNewCheckOutDate = getLocalizedDate(
            newCheckOutDate,
            fullLocale,
            LONG_DATE_FORMAT
        );
        return (
            <div>
                <b>{`${localizedNewCheckInDate} - ${localizedNewCheckOutDate}`}</b>
            </div>
        );
    };

    const getOldGuests = () => {
        const translateOldAdultCount = translate(
            translations,
            'trav-dash/trav-home',
            'messages',
            getAdultsLocalizationKey(`${oldAdultCount}`),
            oldAdultCount
        );
        const translateOldChildCount = translate(
            translations,
            'trav-dash/trav-home',
            'messages',
            getChildrenLocalizationKey(`${oldChildCount}`),
            oldChildCount
        );
        return (
            <div>
                <s>{`${translateOldAdultCount}, ${translateOldChildCount}`}</s>
            </div>
        );
    };

    const getNewGuests = () => {
        const translateNewAdultCount = translate(
            translations,
            'trav-dash/trav-home',
            'messages',
            getAdultsLocalizationKey(newAdultCount),
            newAdultCount
        );
        const translateNewChildCount = translate(
            translations,
            'trav-dash/trav-home',
            'messages',
            getChildrenLocalizationKey(newChildCount),
            newChildCount
        );
        return (
            <div>
                <b>{`${translateNewAdultCount}, ${translateNewChildCount}`}</b>
            </div>
        );
    };

    const loadMessagesTab = () => {
        tripMessageSetSelected({
            actionLocation: MODIFY_BOOKING,
            experienceType: MODIFY_REQUEST,
            reservationUuid,
        });
        refetchConversation();
    };

    const showChangesSection = !!(newCheckInDate || newAdultCount);

    useEffect(() => {
        modifyBookingRequestConfirmationPresented({
            actionLocation: MODIFY_BOOKING,
            experienceType: MODIFY_REQUEST,
            reservationUuid,
        });
    }, [reservationUuid]);

    return (
        <div className="ModifyConfirmation">
            <ModalHeading>
                <SVGIcon markup={CHECK__32} />
                <p className="margin-bottom-0">
                    {translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'modifyBooking.confirmation.title'
                    )}
                </p>
            </ModalHeading>
            <p className="margin-bottom-4x">
                {translate(
                    translations,
                    'trav-dash/trav-home',
                    'messages',
                    'modifyBooking.confirmation.info'
                )}
            </p>
            {showChangesSection && (
                <Fragment>
                    <h4 className="margin-bottom-1x">
                        {translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'modifyBooking.confirmation.yourChangesTitle'
                        )}
                    </h4>
                    {newCheckInDate && (
                        <p
                            className={
                                !newAdultCount
                                    ? 'margin-bottom-4x'
                                    : 'margin-bottom-2x'
                            }
                        >
                            {getOldDates()}
                            {getNewDates()}
                        </p>
                    )}
                    {newAdultCount && (
                        <p className="margin-bottom-4x">
                            {getOldGuests()}
                            {getNewGuests()}
                        </p>
                    )}
                </Fragment>
            )}
            {notes && (
                <Fragment>
                    <h4 className="margin-bottom-1x">
                        {translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'modifyBooking.confirmation.yourMessageTitle'
                        )}
                    </h4>
                    <p className="margin-bottom-4x">{notes}</p>
                </Fragment>
            )}
            <p className="margin-bottom-4x">
                {
                    <b>
                        {translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'modifyBooking.confirmation.alertBoldText'
                        )}
                    </b>
                }{' '}
                {translate(
                    translations,
                    'trav-dash/trav-home',
                    'messages',
                    'modifyBooking.confirmation.alertText'
                )}
            </p>
            <div className="ModifyConfirmation__button-container">
                <Link
                    to={{
                        pathname: `/traveler/th/inbox/conversation/${conversationId}/messages`,
                        state: {
                            backUrl,
                            tabChange: true,
                        },
                    }}
                >
                    <Button
                        label={translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'modifyBooking.confirmation.goToInboxButton'
                        )}
                        onClick={loadMessagesTab}
                    />
                </Link>
                <ButtonPrimary
                    onClick={onCloseModal}
                    label={translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'modifyBooking.confirmation.doneButton'
                    )}
                />
            </div>
        </div>
    );
}

ModifyConfirmation.propTypes = {
    onCloseModal: PropTypes.func,
    requestedFields: PropTypes.shape({
        adults: PropTypes.string,
        children: PropTypes.string,
        checkInDate: PropTypes.string,
        checkOutDate: PropTypes.string,
        notes: PropTypes.string,
    }),
    translations: PropTypes.shape({}),
};
